// ===========================================
// Hybrid Booking Analytics Page
// ===========================================
// Complete analytics dashboard for hybrid booking system
// Phase 2 Priority 12: Reporting & Analytics - Week 4
// Core differentiator: Music 'n Me's unique hybrid lesson model

import React, { useEffect, useState } from 'react';
import { Box, Alert, AlertTitle, Typography, CircularProgress } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import PageHeader from '../../../components/common/PageHeader';
import { useCurrentTerm } from '../../../hooks/useSchoolWeek';
import { describeTerm } from '../../../utils/schoolWeek';
import {
  HybridBookingFilters,
  HybridBookingSummaryCards,
  TimeSlotHeatmap,
  BookingCompletionChart,
  BookingTrendsChart,
  CancellationTable,
} from '../../../components/reports';
import {
  useHybridBookingMetrics,
  useBookingCompletionRates,
  useTimeSlotPopularity,
  useBookingTrends,
  useCancellationPatterns,
} from '../../../hooks/useReports';
import { HybridBookingReportFilters as FilterType } from '../../../api/reports.api';

const HybridBookingAnalyticsPage: React.FC = () => {
  const [filters, setFilters] = useState<FilterType>({});

  // Every query here needs a term (the API answers 400 without one). Default to
  // the school's CURRENT term once terms load; the admin can change it above.
  const { term: currentTerm, terms, isLoading: termLoading, isError: termError } = useCurrentTerm();
  useEffect(() => {
    if (!filters.termId && currentTerm) setFilters((f) => ({ ...f, termId: currentTerm.id }));
  }, [currentTerm, filters.termId]);
  // Name whatever term is selected, not only the current one — picking a past term
  // used to drop the heading back to generic copy. (/qa 2026-09-10)
  const selectedTerm = terms?.find((t) => t.id === filters.termId);

  // Fetch data (each hook is gated on filters.termId)
  const { data: metricsData, isLoading: metricsLoading, isError: metricsError } = useHybridBookingMetrics(filters);
  const { data: completionData, isLoading: completionLoading } =
    useBookingCompletionRates(filters);
  const { data: timeSlotsData, isLoading: timeSlotsLoading } = useTimeSlotPopularity(filters);
  const { data: trendsData, isLoading: trendsLoading } = useBookingTrends(filters);
  const { data: cancellationsData, isLoading: cancellationsLoading } =
    useCancellationPatterns(filters);

  return (
    <Box>
      <PageHeader
        title="Hybrid Booking Analytics"
        subtitle={selectedTerm ? describeTerm(selectedTerm) : "Analyze booking patterns for Music 'n Me's unique hybrid lesson model"}
        icon={<CalendarToday />}
      />

      {/* Core Differentiator Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Music 'n Me's Core Differentiator</AlertTitle>
        <Typography variant="body2">
          Our unique <strong>hybrid lesson model</strong> alternates between group and individual
          sessions. This report analyzes how students book their flexible individual sessions,
          helping you optimize scheduling and improve completion rates.
        </Typography>
      </Alert>

      {/* Filters */}
      <HybridBookingFilters filters={filters} onChange={setFilters} />

      {/* Three states before any chart: terms loading, term lookup failed, metrics failed */}
      {!filters.termId && termLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {!filters.termId && !termLoading && (termError || !currentTerm) && (
        <Alert severity={termError ? 'error' : 'info'} sx={{ mb: 3 }}>
          {termError ? 'The school\'s terms could not be loaded, so no term is selected.' : 'No terms exist yet — create a term to see hybrid booking analytics.'}
        </Alert>
      )}
      {filters.termId && metricsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          The hybrid booking report could not be loaded. Nothing below reflects this term.
        </Alert>
      )}

      {/* Nothing below is rendered until a term is chosen. The five hooks are gated
          `enabled: !!filters.termId`, and a DISABLED React Query v5 query reports
          `isLoading: false` with `data: undefined` — so these cards would read their
          own zero-defaults and paint "0 bookings / 0% completion / Needs improvement"
          as if measured. An absent term is not a measurement of zero. (/qa 2026-09-10) */}
      {filters.termId && (
        <>
          {/* Summary Cards */}
          <HybridBookingSummaryCards summary={metricsData?.summary} isLoading={metricsLoading} />

          {/* Time Slot Heatmap - SHOWPIECE VISUALIZATION */}
          <TimeSlotHeatmap data={timeSlotsData} isLoading={timeSlotsLoading} />

          {/* Booking Completion Chart */}
          <BookingCompletionChart
            data={metricsData?.completionByWeek || completionData}
            isLoading={metricsLoading || completionLoading}
          />

          {/* Booking Trends Chart */}
          <BookingTrendsChart data={trendsData} isLoading={trendsLoading} />

          {/* Cancellation Table */}
          <CancellationTable
            data={cancellationsData?.byWeek}
            isLoading={cancellationsLoading}
          />
        </>
      )}

      {/* Export Placeholder */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Export functionality coming soon (CSV/PDF)
        </Typography>
      </Box>
    </Box>
  );
};

export default HybridBookingAnalyticsPage;
